import { Plus, Trash2 } from 'lucide-react'
import {
  CourseDateRangeRow,
  createEmptyDateRangeRow,
  formatDateRangeLabel,
  sortDateRangeRows,
  todayISODate,
} from '../utils/courseSchedule'

interface CourseDateRangesEditorProps {
  rows: CourseDateRangeRow[]
  onChange: (rows: CourseDateRangeRow[]) => void
  minDate?: string
}

export default function CourseDateRangesEditor({
  rows,
  onChange,
  minDate = todayISODate(),
}: CourseDateRangesEditorProps) {
  const applyRows = (next: CourseDateRangeRow[]) => onChange(sortDateRangeRows(next))

  const updateRow = (id: string, field: 'startDate' | 'endDate', value: string) => {
    applyRows(
      rows.map((r) => {
        if (r.id !== id) return r
        const next = { ...r, [field]: value }
        if (field === 'startDate' && next.endDate && value > next.endDate) {
          next.endDate = value
        }
        return next
      }),
    )
  }

  const removeRow = (id: string) => {
    const next = rows.filter((r) => r.id !== id)
    applyRows(next.length ? next : [createEmptyDateRangeRow()])
  }

  const addRow = () => {
    applyRows([...rows, createEmptyDateRangeRow()])
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Add one or more date ranges. Ranges may overlap. Start date cannot be before today.
      </p>
      <div className="space-y-3">
        {rows.map((row, index) => (
          <div key={row.id} className="p-4 border-2 border-gray-200 rounded-lg bg-gray-50/50 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-800">Batch {index + 1}</span>
              {rows.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeRow(row.id)}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-md"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Start date *</label>
                <input
                  type="date"
                  value={row.startDate}
                  min={minDate}
                  onChange={(e) => updateRow(row.id, 'startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">End date *</label>
                <input
                  type="date"
                  value={row.endDate}
                  min={row.startDate || minDate}
                  onChange={(e) => updateRow(row.id, 'endDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                  required
                />
              </div>
            </div>
            {row.startDate && row.endDate && row.endDate >= row.startDate && (
              <p className="text-xs text-amber-700">{formatDateRangeLabel(row.startDate, row.endDate)}</p>
            )}
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addRow}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100"
      >
        <Plus className="h-4 w-4" />
        Add another date range
      </button>
    </div>
  )
}
